import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {Dataset, Filter, SortConfiguration, TimeGranularity} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import type {Result} from "@vesta/error-handling-remix/result";
import type {
    AnalyticsQueryVariant,
    DataResolverProps,
    RawData,
} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import type {NumberSystems} from "~/utilities/typeDefinitions";
import type {GraphBaseColor} from "~/utilities/analytics/graphColors";

export enum WidgetType {
    multiBarGraph = "ad0b9684-8460-4dac-9695-52250538ac72",
    stackedBarGraph = "e125b8f6-bf87-49b7-ba36-dbf797cc70b7",
    horizontalBarGraph = "b81ec749-5615-4a00-8df3-8ea34834fd50",
    lineGraph = "63b8f630-0350-4f69-92ed-a15050d4eca5",
    areaGraph = "aeecf29e-69fc-40e7-abec-f9f647292dbb",
    comboChart = "f9dc7327-d8ff-4921-9c7f-d0211d195f14",
    numberTile = "82cf1203-ef30-44e0-a35b-84dd3af93103",
    pieChart = "0598c8d6-930a-435f-a3c1-8e25f1e65370",
    donutChart = "3665af28-14c2-4744-b588-2f6dcccc21ee",
    funnelChart = "f3b41bdd-7c2e-4e64-9b41-2c8c1a4d51fa",
    leaderTable = "5d2bb3a0-9c4f-4a1c-bc60-6d3f1c2e8d9b",
    /** Time series with top metric tabs (e.g. Leads / Calls / …), each tab a dual-axis line chart. */
    tabbedTimeSeriesLineChart = "e8c3a91b-2f5d-4c6e-9a01-b47de02f3c10",
    /** 2D matrix of cells, rows × columns, each cell color-coded by a single metric value. */
    heatMap = "f4a91d27-3e5b-4d6c-8a07-19f0c2b3d4e5",
    table = "a0fd3e45-0ed5-4308-9dfa-6e8e32d93437",
    // text = "d5d60556-fc8c-4582-a8ac-f489e22a894e",
    // progressBar = "ba6413fa-aa63-436b-957d-581b2a46024b",
    // gauge = "a6a355a3-09bb-4cc7-8eae-a79c27560e8a",
    // image = "2f4d5eef-78dc-40f0-b0aa-02e98e8f3015",
}

// #region types that will/should be exposed to consumers
// Basically anything that will come from the config of the widget.
export type Widget = {
    id: Uuid;
    datasetId: Uuid;
    datasetResolver: (datasetId: Uuid) => Promise<Result<Dataset>>;
    title: string | null;
    startDate: Iso8601DateTime | null;
    endDate: Iso8601DateTime | null;
    /** Inclusive selected end date for chart labels when endDate is an exclusive query bound. */
    chartEndDate?: Iso8601DateTime | null;
    dimensions: Array<Uuid>;
    metrics: Array<Uuid>;
    editTimeFilters: Array<Filter>;
    viewTimeFilters: Array<Filter>;
    externalFilters: Array<Filter>;
    sortConfiguration: SortConfiguration;
    dataResolver: (dataset: Dataset, props: DataResolverProps) => Promise<Result<RawData>>;
    timeZone: Uuid;
    hideKebabMenu?: boolean;
    hideDateRangeFilter?: boolean;
    /** Optional explanatory text shown by an information button in the widget header. */
    glossary?: string;
    /**
     * When true, pie/donut/line chart hover UI clears once the pointer leaves the chart
     * (and on scroll). Used by the Emotorad performance dashboard so fixed tooltips do not
     * linger over other widgets.
     */
    dismissChartHoverTooltipOnPointerLeave?: boolean;
    /**
     * Optional slice/series palette (index cycles). Used by workspace dashboards that define
     * brand colors in config (e.g. Emotorad Performance).
     */
    chartColorPalette?: ReadonlyArray<string>;
    /** Optional per-widget time bucket control. Intended for date-based periodic charts. */
    granularityOptions?: ReadonlyArray<TimeGranularity>;
    /** Initial time bucket when granularityOptions are configured. */
    defaultGranularity?: TimeGranularity;
    queryVariant: AnalyticsQueryVariant;
} & (
    | {
          type: WidgetType.numberTile;
          numberSystem: NumberSystems;
          decimalPlaces: number;
          spreadValue: boolean;
          valuePrefix: string;
          valueSuffix: string;
      }
    | {
          type:
              | WidgetType.pieChart
              | WidgetType.donutChart
              | WidgetType.multiBarGraph
              | WidgetType.stackedBarGraph
              | WidgetType.horizontalBarGraph
              | WidgetType.table;
          /** Optional labels for table columns, keyed by dataset column id. */
          tableColumnHeaders?: Partial<Record<Uuid, string>>;
      }
    | {
          type: WidgetType.lineGraph | WidgetType.areaGraph | WidgetType.comboChart;
          /** Metrics rendered against the right Y-axis. All other metrics use the left Y-axis. */
          secondaryAxisMetricIds?: Array<Uuid>;
      }
    | {
          type: WidgetType.tabbedTimeSeriesLineChart;
          /**
           * One tab per segment. Each tab renders a line chart with `primaryMetricId` on the left axis
           * and an optional secondary axis. The secondary axis can either be:
           *   - another raw metric value (`secondaryMetricId`), OR
           *   - a frontend-computed conversion ratio (`derivedSecondary`) of the form
           *     `(numeratorMetricId / denominatorMetricId) * multiplyBy`.
           *
           * All metric ids referenced (primary, secondary, derived numerator/denominator) must
           * appear in `widget.metrics` so a single periodic-as-periodic fetch returns every column.
           * Tabs without a secondary entry render a single-line chart on the left axis only.
           */
          metricTabs: Array<{
              tabId: string;
              label: string;
              primaryMetricId: Uuid;
              secondaryMetricId?: Uuid;
              /**
               * Frontend-computed secondary axis. Used when the backend doesn't expose the desired
               * derived metric in periodic-as-periodic mode (e.g. per-day conversion %). Computed
               * per row as `(numerator ?? 0) / denominator * multiplyBy`; rows where the denominator
               * is `0` or null surface as `null` (so the chart leaves a gap instead of plotting NaN).
               */
              derivedSecondary?: {
                  numeratorMetricId: Uuid;
                  denominatorMetricId: Uuid;
                  multiplyBy: number;
                  label: string;
              };
              /** Per-tab line colors (primary = volume, secondary = conversion %). */
              seriesColors?: {
                  primary: string;
                  secondary: string;
              };
          }>;
      }
    | {
          type: WidgetType.funnelChart;
          funnelBaseColor?: GraphBaseColor;
          /** Show numeric value on each bar. Default true. */
          showValues?: boolean;
          /** Show conversion % (relative to first stage) on each bar. Default false. */
          showConversionRate?: boolean;
          /** Show drop-off % (vs previous stage) on each bar. Default false. */
          showDropOffRate?: boolean;
          /** Bar label placement. Default inline (name + value inside bar). */
          funnelLayout?: "inline" | "labeledLeft";
      }
    | {
          type: WidgetType.heatMap;
          /**
           * Dimension placed on the Y-axis. Each unique value in this column becomes one row.
           * Required.
           */
          rowDimensionId: Uuid;
          /**
           * Dimension placed on the X-axis. Each unique value in this column becomes one column.
           * If omitted, the data resolver is expected to fabricate a synthetic time-bucket
           * dimension (see `fetchTimeSeriesViaBucketFanout` in the Emotorad dashboard config) so
           * the chart still has a column dimension to render.
           */
          columnDimensionId?: Uuid;
          /** Single metric whose aggregated value fills each cell (color-encoded). */
          valueMetricId: Uuid;
          /** Display label shown next to the row axis (e.g. `"Regions"`). */
          rowAxisLabel: string;
          /** Display label shown beneath the column axis (e.g. `"Date"`). */
          columnAxisLabel: string;
          /**
           * Optional complete row-axis value list (e.g. all zone names). Missing rows from the API
           * are filled with 0. Overrides `rawData.heatMapFixedRowKeys` when both are set.
           */
          fixedRowKeys?: ReadonlyArray<string>;
          /** Color theme used to encode cell values. Defaults to `"sequentialBlue"`. */
          colorScheme?: "sequentialBlue" | "sequentialGreen";
          /** Show numeric values inside cells. Default false; hover tooltip still shows values. */
          showCellValues?: boolean;
      }
    | {
          type: WidgetType.leaderTable;
          /**
           * Workspace-defined hierarchy resolver. Given the leaf user id present on a raw data row,
           * returns the ordered (top-of-tree → leaf) list of `{id, label}` pairs for that row.
           * `id` is a stable unique key used for AG Grid grouping; `label` is the display string.
           * Returning `null` skips the row (e.g. user not found, or has no leader).
           * The framework intentionally knows nothing about how the chain is computed — the dashboard
           * config closes over its own `users` / `userTemplates` data when wiring this in.
           */
          resolveRowPath: (leafUserId: Uuid) => Array<{id: string; label: string}> | null;
          /** Header for each path-level group column (top → leaf). Length must match returned path. */
          pathColumnHeaders: Array<string>;
          /** Display label for each metric column (parallel to widget.metrics). */
          metricColumnLabels: Array<string>;
          /** Dataset column id of the user dimension that backend returns per row. */
          userDimensionColumnId: Uuid;
      }
);
// #endregion types that will/should be exposed to consumers
